import { useEffect } from 'react';

/**
 * Minimal SEO hook — updates document title, description meta, canonical,
 * and Open Graph tags on route change. Keeps things dependency-free (no
 * react-helmet) and safe to call repeatedly.
 */
const SITE_ORIGIN = 'https://www.linguisticquest.com';

function upsertMeta(selector, attrs) {
    let el = document.head.querySelector(selector);
    if (!el) {
        el = document.createElement('meta');
        Object.entries(attrs.create).forEach(([k, v]) => el.setAttribute(k, v));
        document.head.appendChild(el);
    }
    el.setAttribute('content', attrs.content);
}

function upsertLink(rel, href) {
    let el = document.head.querySelector(`link[rel="${rel}"]`);
    if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        document.head.appendChild(el);
    }
    el.setAttribute('href', href);
}

function upsertJsonLd(id, payload) {
    let el = document.head.querySelector(`script[type="application/ld+json"][data-seo-id="${id}"]`);
    if (!el) {
        el = document.createElement('script');
        el.setAttribute('type', 'application/ld+json');
        el.setAttribute('data-seo-id', id);
        document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(payload);
}

export function useSEO({ title, description, path, image, jsonLd, jsonLdId }) {
    useEffect(() => {
        if (title) document.title = title;
        if (description) {
            upsertMeta('meta[name="description"]', {
                create: { name: 'description' },
                content: description,
            });
            upsertMeta('meta[property="og:description"]', {
                create: { property: 'og:description' },
                content: description,
            });
            upsertMeta('meta[name="twitter:description"]', {
                create: { name: 'twitter:description' },
                content: description,
            });
        }
        if (title) {
            upsertMeta('meta[property="og:title"]', {
                create: { property: 'og:title' },
                content: title,
            });
            upsertMeta('meta[name="twitter:title"]', {
                create: { name: 'twitter:title' },
                content: title,
            });
        }
        const fullUrl = `${SITE_ORIGIN}${path || (typeof window !== 'undefined' ? window.location.pathname : '/')}`;
        upsertLink('canonical', fullUrl);
        upsertMeta('meta[property="og:url"]', {
            create: { property: 'og:url' },
            content: fullUrl,
        });
        if (image) {
            upsertMeta('meta[property="og:image"]', {
                create: { property: 'og:image' },
                content: image.startsWith('http') ? image : `${SITE_ORIGIN}${image}`,
            });
            upsertMeta('meta[name="twitter:image"]', {
                create: { name: 'twitter:image' },
                content: image.startsWith('http') ? image : `${SITE_ORIGIN}${image}`,
            });
        }
        if (jsonLd && jsonLdId) {
            upsertJsonLd(jsonLdId, jsonLd);
        }
    }, [title, description, path, image, jsonLd, jsonLdId]);
}

export function useNoIndex(title) {
    useEffect(() => {
        if (typeof document === 'undefined') return;
        if (title) document.title = title;
        let robots = document.head.querySelector('meta[name="robots"]');
        if (!robots) {
            robots = document.createElement('meta');
            robots.setAttribute('name', 'robots');
            document.head.appendChild(robots);
        }
        const previous = robots.getAttribute('content');
        robots.setAttribute('content', 'noindex,follow');
        return () => {
            // Restore the site default when leaving an auth/private page.
            robots.setAttribute('content', previous || 'index,follow,max-image-preview:large');
        };
    }, [title]);
}

export default useSEO;
